<template>
  <a
    :href="disabled ? null : (href || '#')"
    :target="target"
    :rel="rel"
    :class="classes"
    @mousedown="press"
    @mouseup="release"
    @touchstart="press"
    @touchend="release"
    @click.prevent
  >
    <span>
      <slot></slot>
    </span>
  </a>
</template>

<style lang="scss" scoped>
  $bgColor: module.$main;
  $fontColor: module.$white;
  $hoverColor: module.tone(module.$main, 2);
  a{
    display: inline-block;
    position: relative;
    user-select: none;
    background: $bgColor;
    border-radius: 999rem;
    z-index: 1;
    &.orange{
      background: module.$orange;
      &:before{
        background: rgba(module.$orange, .32);
      }
      span{
        &:after{
          background: module.tone(module.$orange, 2);
        }
      }
    }
    &.blue{
      background: module.$blue;
      &:before{
        background: rgba(module.$blue, .32);
      }
      span{
        &:after{
          background: module.tone(module.$blue, 2);
        }
      }
    }
    &.green{
      $color: module.$green;
      background: $color;
      &:before{
        background: rgba($color, .32);
      }
      span{
        &:after{
          background: module.tone($color, 2);
        }
      }
    }
    &.purple{
      $color: #40439d;
      background: $color;
      &:before{
        background: rgba($color, .32);
      }
      span{
        &:after{
          background: module.tone($color, 2);
        }
      }
    }
    span{
      position: relative;
      @include module.flex();
      align-items: center;
      justify-content: center;
      padding: .8rem 2.4rem;
      border-radius: 999rem;
      gap: .4rem;
      color: $fontColor;
      overflow: hidden;
      z-index: 3;
      &:after{
        content: '';
        width: 0;
        height: 100%;
        background: $hoverColor;
        position: absolute;
        top: 0;
        right: 0;
        transition: width 200ms module.$ease;
        z-index: -1;
      }
    }
    &:before{
      content: '';
      width: 100%;
      height: 100%;
      background: rgba($bgColor, .32);
      position: absolute;
      top: 0;
      left: 0;
      border-radius: 999rem;
      pointer-events: none;
      z-index: -1;
    }
    &:hover{
      span{
        &:after{
          width: 100%;
          left: 0;
          right: auto;
        }
      }
    }
    &.pressed{
      animation: onPress 160ms forwards module.$ease;
      span{
        transform: scale(1.04);
        &:after{
          width: 100%;
        }
      }
      @keyframes onPress {
        0%{
          transform: scale(1);
        }
        100%{
          transform: scale(.96);  
        }
      }
      &:before{
        animation: onPressBefore 160ms forwards module.$ease;
        @keyframes onPressBefore {
          0%{
            transform: scale(1);
            opacity: 1;
          }
          100%{
            transform: scale(1.3);
            opacity: 0;  
          }
        }
      }
    }
  }
</style>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  href: String,
  target: String,
  rel: String,
  callback: Function,
  disabled: Boolean
})

const pressed = ref(false)
const released = ref(false)

const classes = computed(() => ({
  pressed: pressed.value,
  released: released.value,
  disabled: props.disabled
}))

function press() {
  if (props.disabled) return

  pressed.value = true
  setTimeout(() => {
    pressed.value = false
  }, 160)
}

function release() {
  if (props.disabled) return
  released.value = true
  setTimeout(() => {
    released.value = false
    if (props.callback) {
      props.callback()
      return
    }
    if (props.href) {
      if (props.target === '_blank') {
        window.open(props.href, '_blank')
      } else {
        window.location.href = props.href
      }
    }
  }, 160)
}
</script>